import { Stack, IconButton, alpha, useTheme } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import {
  ShoppingCart as ShoppingCartIcon,
  ShoppingCartOutlined as ShoppingCartOutlinedIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
} from '@mui/icons-material';

interface CollectionToggleButtonsProps {
  isWishlist: boolean;
  isFavorite: boolean;
  onToggleWishlist: () => void;
  onToggleFavorite: () => void;
  isToggling?: boolean;
  size?: 'small' | 'medium' | 'large';
}

const CollectionToggleButtons: React.FC<CollectionToggleButtonsProps> = ({
  isWishlist,
  isFavorite,
  onToggleWishlist,
  onToggleFavorite,
  isToggling = false,
  size = 'medium'
}) => {
  const theme = useTheme();
  const { isAuthenticated } = useAuth();
  const { showError } = useNotification();

  const handleAction = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      showError('Please sign in to manage your collection');
      return;
    }
    action();
  };

  return (
    <Stack direction="row" spacing={1.5}>
      {/* Wishlist Toggle */}
      <IconButton
        size={size}
        onClick={(e) => handleAction(e, onToggleWishlist)}
        disabled={isToggling}
        sx={{
          backgroundColor: isWishlist 
            ? theme.palette.info.main 
            : alpha(theme.palette.info.main, 0.1),
          color: isWishlist ? '#fff' : theme.palette.info.main,
          '&:hover': {
            backgroundColor: isWishlist 
              ? theme.palette.info.dark 
              : alpha(theme.palette.info.main, 0.2),
          },
          boxShadow: 2,
        }}
        title={isWishlist ? "Remove from Buy List" : "Add to Buy List"}
      >
        {isWishlist ? <ShoppingCartIcon fontSize={size} /> : <ShoppingCartOutlinedIcon fontSize={size} />}
      </IconButton>

      {/* Favorite Toggle */}
      <IconButton
        size={size}
        onClick={(e) => handleAction(e, onToggleFavorite)}
        disabled={isToggling}
        sx={{
          backgroundColor: isFavorite 
            ? theme.palette.error.main 
            : alpha(theme.palette.error.main, 0.1),
          color: isFavorite ? '#fff' : theme.palette.error.main,
          '&:hover': {
            backgroundColor: isFavorite 
              ? theme.palette.error.dark 
              : alpha(theme.palette.error.main, 0.2),
          },
          boxShadow: 2,
        }}
        title={isFavorite ? "Remove from Favorites" : "Add to Favorites"}
      >
        {isFavorite ? <FavoriteIcon fontSize={size} /> : <FavoriteBorderIcon fontSize={size} />}
      </IconButton>
    </Stack>
  );
};

export default CollectionToggleButtons;
